import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, History, X } from 'lucide-react';
import { useSavedWords } from '../../lib/useSavedWords';
import { DictionaryService } from '../../services/dictionaryService';
import Reveal from '../Reveal';

// Both lists live in this browser only; the copy says so
const YourWords = () => {
  const { saved, toggle } = useSavedWords();
  const [recent] = useState(() => DictionaryService.getRecentWords());

  return (
    <section aria-labelledby="your-words-heading" className="wrap pt-16 md:pt-24">
      <Reveal>
        <h2 id="your-words-heading" className="font-display text-4xl leading-tight text-ink md:text-5xl">
          Your words
        </h2>
        <p className="mt-3 max-w-xl text-lg text-ink-soft">
          Words you save and look up are kept in this browser, so they’re here next time you visit on this device.
        </p>
      </Reveal>

      <Reveal delay={80} className="mt-10 grid gap-4 md:grid-cols-2">
        <div className="rounded-[1.75rem] bg-butter p-6 sm:p-8">
          <h3 className="flex items-center gap-2.5 font-display text-2xl text-ink">
            <Bookmark size={20} aria-hidden="true" />
            Words I’m learning
          </h3>
          {saved.length > 0 ? (
            <ul className="mt-5 flex flex-wrap gap-2">
              {saved.map((word) => (
                <li key={word} className="inline-flex items-center rounded-full bg-surface/85 pl-4 text-ink">
                  <Link to={`/define/${encodeURIComponent(word)}`} className="py-1.5 text-[1.05rem] hover:underline">
                    {word}
                  </Link>
                  <button
                    type="button"
                    onClick={() => toggle(word)}
                    aria-label={`Remove ${word} from your words`}
                    className="ml-1 grid h-9 w-9 place-items-center rounded-full text-ink-soft transition-colors hover:bg-ink/5 hover:text-ink"
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 max-w-sm leading-relaxed text-ink">
              Nothing saved yet. Use the bookmark on any word card or word page and it appears here.
            </p>
          )}
        </div>

        <div className="rounded-[1.75rem] bg-bluebell p-6 sm:p-8">
          <h3 className="flex items-center gap-2.5 font-display text-2xl text-ink">
            <History size={20} aria-hidden="true" />
            Recently explored
          </h3>
          {recent.length > 0 ? (
            <ul className="mt-5 flex flex-wrap gap-2">
              {recent.map((word) => (
                <li key={word}>
                  <Link
                    to={`/define/${encodeURIComponent(word)}`}
                    className="inline-block rounded-full bg-surface/85 px-4 py-1.5 text-[1.05rem] text-ink transition-[background-color,transform] hover:bg-surface active:scale-95"
                  >
                    {word}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 max-w-sm leading-relaxed text-ink">
              The words you look up will show here, so you can find them again.
            </p>
          )}
        </div>
      </Reveal>
    </section>
  );
};

export default YourWords;
